<template>
  <div class="experiences-landing-page">
    <section class="hero">
      <h1>{{ t('Discover Experiences & Activities') }}</h1>
      <p class="hero-subtitle">{{ t('Find tours, activities, and things to do worldwide. Book your next adventure.') }}</p>
      <div class="hero-search">
        <div class="search-input-wrapper">
          <span class="icon icon-search search-icon"></span>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('Search for a city or destination...')"
            class="search-input"
            @input="onSearchInput"
            @focus="showSuggestions = true"
          />
          <div v-if="showSuggestions && suggestions.length" class="search-suggestions">
            <NuxtLink
              v-for="suggestion in suggestions"
              :key="suggestion.id"
              :to="localePath(`/experiences/${suggestion.slug}`)"
              class="search-suggestion-item"
              @click="showSuggestions = false"
            >
              <span class="icon icon-map-point suggestion-icon"></span>
              <div>
                <div class="suggestion-name">{{ suggestion.name }}</div>
                <div class="suggestion-country" v-if="suggestion.country_name">{{ suggestion.country_name }}</div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="featured-section" v-if="searchStore.topActivities?.length">
      <h2>{{ t('Top-Rated Experiences') }}</h2>
      <div class="featured-grid">
        <NuxtLink
          v-for="activity in searchStore.topActivities"
          :key="activity.id"
          :to="localePath(`/experiences/${(activity.geo_place?.slug && activity.geo_place.slug !== 'null') ? activity.geo_place.slug : 'results'}/${activity.id}-${activity.slug}`)"
          class="featured-card"
        >
          <div class="featured-card__image">
            <img
              v-if="activity.cover_image_url"
              :src="imageUrlWithProxy(activity.cover_image_url, 400, 280, 'fill')"
              :alt="activity.normalized_name || activity.name"
              loading="lazy"
              width="400" height="280"
            />
            <PhotoPlaceholder v-else />
          </div>
          <div class="featured-card__content">
            <div class="featured-card__badges">
              <span v-if="getCategoryName(activity)" class="badge badge--category">{{ getCategoryName(activity) }}</span>
              <span v-if="activity.duration" class="badge badge--duration">{{ formatDuration(activity.duration) }}</span>
              <span v-if="hasFreeCancellation(activity)" class="badge badge--cancellation">{{ t('Free cancellation') }}</span>
            </div>
            <h3 class="featured-card__title">{{ activity.normalized_name || activity.name }}</h3>
            <div class="featured-card__location" v-if="activity.location">
              {{ activity.location.city_name }}
            </div>
            <div class="featured-card__footer">
              <div class="featured-card__rating" v-if="activity.avg_rating > 0">
                <span class="rating-score">{{ Number(activity.avg_rating).toFixed(1) }}</span>
                <span class="rating-label">
                  {{ activity.avg_rating >= 4.5 ? t('Excellent') : activity.avg_rating >= 3.5 ? t('Very Good') : t('Good') }}
                </span>
                <span class="rating-count" v-if="activity.overall_rating">{{ Number(activity.overall_rating).toFixed(1) }}/5</span>
              </div>
              <div class="featured-card__price" v-if="activity.price_from">
                <span class="price-label">{{ t('From') }}</span>
                <span class="price-value">{{ activity.price_from.formatted }}</span>
              </div>
            </div>
          </div>
        </NuxtLink>
      </div>
    </section>

    <section class="popular-cities" v-if="popularCities.length">
      <h2>{{ t('Popular Destinations') }}</h2>
      <div class="cities-grid">
        <NuxtLink
          v-for="city in popularCities"
          :key="city.slug"
          :to="localePath(`/experiences/${city.slug}`)"
          class="city-card"
        >
          <img
            :src="city.img"
            :alt="city.name"
            class="city-card__image"
            loading="lazy"
            @error="onCityImgError"
          />
          <div class="city-card__overlay">
            <span class="city-card__name">{{ city.name }}</span>
          </div>
        </NuxtLink>
      </div>
    </section>

    <section class="browse-all">
      <NuxtLink :to="localePath('/experiences/destinations')" class="browse-all__link">
        {{ t('Browse all experience destinations') }}
      </NuxtLink>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHreflangTags } from '~/composables/useSeo'
import { useSearchStore } from '~/store/search'
import { imageUrlWithProxy } from '~/utils/imageproxy'
import dayjs from '~/utils/dayjs'

const { $siteConfig } = useNuxtApp()
const config = useRuntimeConfig()
const searchStore = useSearchStore()
const localePath = useLocalePath()
const { t } = useI18n()

useHreflangTags('/experiences')

const searchQuery = ref('')
const suggestions = ref<any[]>([])
const showSuggestions = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const popularCities = [
  { name: 'New York', slug: 'us-ny-new-york', img: '/images/cities/new-york.webp' },
  { name: 'Miami', slug: 'us-fl-miami-beach', img: '/images/cities/miami.webp' },
  { name: 'Los Angeles', slug: 'us-ca-los-angeles', img: '/images/cities/los-angeles.webp' },
  { name: 'Las Vegas', slug: 'us-nv-las-vegas', img: '/images/cities/las-vegas.webp' },
  { name: 'San Francisco', slug: 'us-ca-san-francisco', img: '/images/cities/san-francisco.webp' },
  { name: 'San Diego', slug: 'us-ca-san-diego', img: '/images/cities/san-diego.webp' },
]

function onSearchInput() {
  if (searchTimeout) clearTimeout(searchTimeout)
  if (searchQuery.value.length < 2) {
    suggestions.value = []
    return
  }
  searchTimeout = setTimeout(async () => {
    const results = await searchStore.getLocations({ fts: searchQuery.value, domain: 'activity' })
    suggestions.value = (results as any)?.places || (results as any) || []
  }, 300)
}

function formatDuration(seconds: number) {
  return dayjs.duration(seconds * 1000).humanize()
}

function getCategoryName(activity: any) {
  if (activity.categories?.length) return activity.categories[0].name
  return activity.category_name || ''
}

function hasFreeCancellation(activity: any) {
  return activity.cancellation_policies?.some(
    (p: any) => p.refund_value == 100 && p.refund_type === 'percentage'
  )
}

function onCityImgError(e: Event) {
  const card = (e.target as HTMLImageElement).closest('.city-card') as HTMLElement | null
  if (card) card.style.display = 'none'
}

function onClickOutside(e: Event) {
  const wrapper = document.querySelector('.search-input-wrapper')
  if (wrapper && !wrapper.contains(e.target as Node)) {
    showSuggestions.value = false
  }
}

onMounted(() => {
  searchStore.searchTopActivities()
  document.addEventListener('click', onClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
  if (searchTimeout) clearTimeout(searchTimeout)
})

const brand = computed(() => $siteConfig?.value?.brand || 'Tripening')

useHead({
  title: computed(() => `Discover Experiences & Activities Worldwide | ${brand.value}`),
  link: [
    { rel: 'canonical', href: `${config.public.baseURL}/experiences` }
  ],
  meta: [
    {
      name: 'description',
      content: computed(() => `Explore tours, activities, and things to do worldwide on ${brand.value}. Read reviews, compare prices, and book your perfect experience.`)
    },
    { property: 'og:title', content: computed(() => `Discover Experiences & Activities | ${brand.value}`) },
    { property: 'og:description', content: computed(() => `Explore tours and activities worldwide on ${brand.value}. Book your perfect experience.`) },
    { property: 'og:url', content: `${config.public.baseURL}/experiences` },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' }
  ]
})

definePageMeta({
  layout: 'search'
})
</script>

<style scoped lang="scss">
.experiences-landing-page {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: 0 var(--page-horizontal-padding);
}

.hero {
  text-align: center;
  padding: 48px 0 40px;

  h1 {
    font-size: 34px;
    font-weight: 700;
    line-height: 48px;
    color: var(--black);
    margin-bottom: 12px;

    @media screen and (max-width: 768px) {
      font-size: 26px;
      line-height: 36px;
    }
  }

  .hero-subtitle {
    font-size: 16px;
    color: var(--dark-grey);
    line-height: 24px;
    margin-bottom: 32px;

    @media screen and (max-width: 768px) {
      margin-bottom: 24px;
    }
  }
}

.hero-search {
  max-width: 560px;
  margin: 0 auto;
}

.search-input-wrapper {
  position: relative;

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
    color: var(--dark-grey);
    z-index: 1;
  }

  .search-input {
    width: 100%;
    padding: 14px 20px 14px 48px;
    border: 2px solid var(--grey);
    border-radius: 10px;
    font-size: 16px;
    line-height: 24px;
    background: var(--white);
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: var(--black);
    }

    &::placeholder {
      color: var(--dark-grey);
    }
  }
}

.search-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--white);
  border: 1px solid var(--grey-border);
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06);
  z-index: 10;
  max-height: 320px;
  overflow-y: auto;
}

.search-suggestion-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background: var(--light-grey);
  }

  .suggestion-icon {
    font-size: 18px;
    color: var(--blue);
  }

  .suggestion-name {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: var(--black);
  }

  .suggestion-country {
    font-size: 14px;
    line-height: 20px;
    color: var(--dark-grey);
  }
}

.featured-section, .popular-cities {
  padding: 32px 0;

  h2 {
    font-size: 24px;
    font-weight: 600;
    line-height: 36px;
    margin-bottom: 20px;
    color: var(--black);

    @media screen and (max-width: 768px) {
      font-size: 20px;
      line-height: 32px;
    }
  }
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media screen and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.featured-card {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 0 2px var(--grey-border);
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 0 0 2px var(--light-grey-border);
  }

  &__image {
    height: 200px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: var(--black);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__location {
    font-size: 14px;
    line-height: 20px;
    color: var(--dark-grey);
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: auto;
    padding-top: 8px;
  }

  &__rating {
    display: flex;
    align-items: center;
    gap: 8px;

    .rating-score {
      background: var(--green);
      color: var(--white);
      width: 40px;
      height: 40px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
    }

    .rating-label {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: var(--black);
    }

    .rating-count {
      font-size: 12px;
      line-height: 16px;
      color: var(--dark-grey);
    }
  }

  &__price {
    text-align: right;

    .price-label {
      font-size: 12px;
      line-height: 16px;
      color: var(--dark-grey);
      display: block;
    }

    .price-value {
      font-size: 20px;
      font-weight: 600;
      line-height: 24px;
      color: var(--black);
    }
  }
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  &--category {
    background: var(--blue-highlight);
    color: var(--blue);
  }

  &--duration {
    background: var(--light-grey);
    color: var(--dark-grey);
  }

  &--cancellation {
    background: #e8f5e9;
    color: #2e7d32;
  }
}

.cities-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.browse-all {
  text-align: center;
  padding: 32px 0 16px;

  &__link {
    display: inline-block;
    color: var(--blue);
    font-size: 16px;
    font-weight: 600;
    text-decoration: none;
    padding: 12px 24px;
    border: 1px solid var(--blue);
    border-radius: 10px;
    transition: background-color 0.15s;

    &:hover {
      background-color: rgba(25, 133, 249, 0.06);
    }
  }
}

.city-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  height: 180px;
  display: block;

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 60%);
    display: flex;
    align-items: flex-end;
    padding: 16px;
  }

  &__name {
    color: white;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
  }

  &:hover &__overlay {
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.1) 60%);
  }
}
</style>
